// @ts-check

/// <reference types="vite/client" />

import * as vue from "vue";
import { registerCustomElement } from "./registration";
import { enrichProps, extractComponentName } from "./utils";

/**
 * Registers all components in the components directory.
 *
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 * @param {Record<string, string[]>} aliases - Aliases for the component names.
 */
export async function registerComponents(props, aliases) {
  /**
   * @type {Record<string, import("vue").DefineComponent>}
   */
  const components = import.meta.glob("../components/**/Sp*.ce.vue", { eager: true });

  for (const [path, component] of Object.entries(components)) {
    registerComponent(component, path, aliases, props);
  }
}

/**
 * Registers a component.
 *
 * @param {import("vue").DefineComponent} component - The component.
 * @param {string} path - The path to the component.
 * @param {Record<string, string[]>} aliases - Aliases for the component names.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
export function registerComponent(component, path, aliases, props) {
  const fileName = path.split("/").at(-1);

  if (!fileName) {
    console.warn(`Invalid component file name: ${path}`);
    return;
  }

  const elementConstructor = defineCustomElement(component.default, fileName, props);
  registerCustomElement(fileName, elementConstructor);

  const componentName = extractComponentName(fileName);
  if (componentName in aliases) {
    registerAliases(aliases[componentName], component, props);
  }
}

/**
 * Registers the aliases for the component.
 *
 * @param {string[]} aliases - Aliases for the component.
 * @param {import("vue").DefineComponent} component - The component.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
function registerAliases(aliases, component, props) {
  aliases.forEach((alias) => registerAlias(alias, component, props));
}

/**
 * Registers an alias for the component.
 *
 * @param {string} alias - The alias for the component.
 * @param {import("vue").DefineComponent} component - The component.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
function registerAlias(alias, component, props) {
  const elementConstructor = defineCustomElement(component.default, alias, props);
  registerCustomElement(alias, elementConstructor);
}

/**
 * Defines a custom element.
 * If applicable, the props passed to the custom element will be used as the default values for the component props.
 *
 * @param {import("vue").DefineComponent} component
 * @param {string} fileName
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 * @returns {import("vue").VueElementConstructor<{}>}
 */
export function defineCustomElement(component, fileName, props) {
  // @ts-ignore
  return vue.defineCustomElement({
    ...component,
    props: enrichProps(component, fileName, props),
  });
}
